import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAllNodes, selectFilteredPoints } from './slice/mapSelectors';
import { fromLonLat } from 'ol/proj';
import { BuyOrSell } from '../types/api/PriceComponent';
import { colors } from '../utils/colors';
import { useMapLayer } from '../hooks/useMapLayer';
import { FeatureLike } from '../utils/geoJsonUtils';
import { selectShowPriceComponents } from '../plotter/plotterSelectors';

const getPriceComponentStyle = () => {
  return {
    'circle-radius': 12,
    'circle-fill-color': ['get', 'fillColor'],
    'circle-stroke-color': ['get', 'color'],
    'circle-stroke-width': 2,
    'circle-rotate-with-view': false,
    'circle-opacity': 0.8,
  };
};

export type MapPriceComponentsProps = {
  zIndex: number;
};

export const MapPriceComponents: React.FC<MapPriceComponentsProps> = ({
  zIndex,
}) => {
  const points = useSelector(selectFilteredPoints);
  const nodes = useSelector(selectAllNodes);
  const showPriceComponents = useSelector(selectShowPriceComponents);

  const features = useMemo<FeatureLike[]>(() => {
    return showPriceComponents
      ? Object.values(points)
          .filter((point) => point.price_components?.length > 0)
          .map((point) => {
            const node = nodes[point.node ?? ''];
            return { point, node };
          })
          .filter(({ node }) => Boolean(node))
          .map(({ point, node }) => {
            const priceTypes = new Set<BuyOrSell>(
              point.price_components?.map((pc) => pc.buy_sell) ?? [],
            );
            const color =
              priceTypes.has(BuyOrSell.BOTH) ||
              (priceTypes.has(BuyOrSell.BUY) && priceTypes.has(BuyOrSell.SELL))
                ? colors.elements.bothMarketColor
                : priceTypes.has(BuyOrSell.BUY)
                ? colors.elements.buyMarketColor
                : colors.elements.sellMarketColor;
            const fillColor = `${color}50`;
            const feature = {
              type: 'Feature' as 'Feature',
              geometry: {
                type: 'Point',
                coordinates: fromLonLat([Number(node.lng), Number(node.lat)]),
              },
              properties: {
                fillColor,
                color,
                id: node.id,
                key: `node-${node.id}`,
              },
            };
            return feature;
          })
      : [];
  }, [points, nodes, showPriceComponents]);

  const style = useMemo(() => getPriceComponentStyle(), []);

  useMapLayer(features, { zIndex, style });

  return <></>;
};
